import { ChatProjectFormDialog } from '@/components/chat/ChatProjectFormDialog';
import { ChatProjectsSection } from '@/components/chat/ChatProjectsSection';
import { ChatSearchDialog } from '@/components/chat/ChatSearchDialog';
import { DraggableChatRow } from '@/components/chat/DraggableChatRow';
import { AppSidebar, type SidebarGroup, type SidebarItem } from '@/components/layout/AppSidebar';
import { FamilySitesSection } from '@/components/layout/FamilySitesSection';
import { UserChip } from '@/components/layout/UserChip';
import { api } from '@/lib/api';
import { useAppSettings } from '@/lib/appSettings';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { cn } from '@/lib/cn';
import { IS_MAC } from '@/lib/platform';
import { useIsMobile } from '@/lib/useIsMobile';
import { useSidebarCollapsed } from '@/lib/useSidebarCollapsed';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { ChatProject, ChatSession } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { BookOpen, LayoutDashboard, Plus, /* Presentation, */ Search } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function ChatLayout() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [projectDialog, setProjectDialog] = useState<
    { open: false } | { open: true; project?: ChatProject }
  >({ open: false });
  const { collapsed, toggle } = useSidebarCollapsed();
  const isMobile = useIsMobile();
  // 모바일 첫 진입 시 사이드바 자동 collapse (1회만 강제). 이후 사용자 토글은 그대로 존중.
  const mobileInitialCollapsedRef = useRef(false);
  useEffect(() => {
    if (isMobile && !mobileInitialCollapsedRef.current && !collapsed) {
      mobileInitialCollapsedRef.current = true;
      toggle();
    }
    if (!isMobile) mobileInitialCollapsedRef.current = false;
  }, [isMobile, collapsed, toggle]);

  // 모바일 + 사이드바 열린 상태에서 라우트가 바뀌면 자동 close (메뉴 클릭 후 채팅 화면 보이게).
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      if (isMobile && !collapsed) toggle();
    }
  }, [location.pathname, isMobile, collapsed, toggle]);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) navigate({ to: '/login' });
  }, [isLoading, isAuthenticated, navigate]);

  // ⌘K / Ctrl+K 로 채팅 검색 토글. 입력 요소에 있을 때도 동작 (command palette 관례).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sessionsQuery = useQuery({
    queryKey: ['chat', 'sessions'],
    queryFn: api.chatSessionList,
    enabled: isAuthenticated,
    staleTime: 5_000,
  });
  const appSettings = useAppSettings();
  const wikiMenuVisible = appSettings.data?.wikiEnabled !== false;
  const branding = useBranding();

  const familySitesQuery = useQuery({
    queryKey: ['family-sites'],
    queryFn: api.familySiteList,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const familySites = familySitesQuery.data?.sites ?? [];

  // 대시보드 메뉴는 본인 소유 + 공유받은 게 둘 다 0 이면 자체 미렌더 (사이드바 노이즈 감소).
  // queryKey 는 /dashboards 목록 페이지가 쓰는 것과 동일 — 캐시 공유로 페이지 진입 시 즉시 표시.
  const dashboardsMineQuery = useQuery({
    queryKey: ['dashboards', 'mine'],
    queryFn: api.dashboardListMine,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const dashboardsSharedQuery = useQuery({
    queryKey: ['dashboards', 'shared'],
    queryFn: api.dashboardListShared,
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
  const dashboardMenuVisible =
    (dashboardsMineQuery.data?.dashboards.length ?? 0) +
      (dashboardsSharedQuery.data?.dashboards.length ?? 0) >
    0;

  // 드래그 sensors. mouse + touch + keyboard a11y. 작은 이동 (8px / long-press 200ms)
  // 미만은 click 으로 떨어뜨려 chat row 의 라우트 이동을 깨지 않는다.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const moveMutation = useMutation({
    mutationFn: ({ sessionId, projectId }: { sessionId: string; projectId: string | null }) =>
      api.chatMoveSession(sessionId, { projectId }),
    onMutate: async ({ sessionId, projectId }) => {
      await qc.cancelQueries({ queryKey: ['chat', 'sessions'] });
      const prev = qc.getQueryData<{ sessions: ChatSession[] }>(['chat', 'sessions']);
      if (prev) {
        qc.setQueryData(['chat', 'sessions'], {
          sessions: prev.sessions.map((s) => (s.id === sessionId ? { ...s, projectId } : s)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['chat', 'sessions'], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['chat', 'projects'] });
    },
  });

  const onDragEnd = (e: DragEndEvent) => {
    const over = e.over;
    if (!over) return;
    const overData = over.data.current as { type?: string; projectId?: string } | undefined;
    const activeData = e.active.data.current as
      | {
          type?: string;
          sessionId?: string;
          projectId?: string | null;
        }
      | undefined;

    // 채팅 세션 → 프로젝트 이동.
    if (!activeData?.sessionId || overData?.type !== 'project' || !overData.projectId) return;
    if (activeData.projectId === overData.projectId) return;
    moveMutation.mutate({ sessionId: activeData.sessionId, projectId: overData.projectId });
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground-muted text-sm">
        로딩 중…
      </div>
    );
  }

  const sessions = sessionsQuery.data?.sessions ?? [];
  // 즐겨찾기 (프로젝트 무관) → 프로젝트별 → 최근 채팅 (미분류 & !즐겨찾기).
  const favorites = sessions.filter((s) => s.isFavorite);
  const unfiled = sessions.filter((s) => !s.isFavorite && !s.projectId);
  const inProjects = sessions.filter((s) => !s.isFavorite && s.projectId);

  const renderChatRow = (s: ChatSession) => (
    <DraggableChatRow
      key={s.id}
      session={s}
      activePath={location.pathname}
      renamingId={renamingId}
      onStartRename={(id) => setRenamingId(id)}
      onDoneRename={() => setRenamingId(null)}
    />
  );

  const projectHeaderAction = (
    <button
      type="button"
      onClick={() => setProjectDialog({ open: true })}
      aria-label="새 프로젝트"
      className="size-5 inline-grid place-items-center rounded text-foreground-muted hover:bg-surface-muted hover:text-foreground"
    >
      <Plus className="size-3.5" />
    </button>
  );

  const groups: SidebarGroup[] = [
    {
      items: [
        { label: '새 채팅', to: '/chat', icon: Plus },
        {
          label: '채팅 검색',
          onClick: () => setSearchOpen(true),
          icon: Search,
          shortcut: IS_MAC ? '⌘K' : 'Ctrl K',
        },
        ...(wikiMenuVisible
          ? [{ label: '지식 위키', to: '/wikis', icon: BookOpen } as SidebarItem]
          : []),
      ],
    },
    ...(favorites.length > 0
      ? [
          {
            heading: '즐겨찾기',
            items: [],
            renderContent: <ul className="space-y-0.5">{favorites.map(renderChatRow)}</ul>,
          } satisfies SidebarGroup,
        ]
      : []),
    ...(familySites.length > 0
      ? [
          {
            heading: '패밀리 사이트',
            items: [],
            renderContent: <FamilySitesSection sites={familySites} />,
          } satisfies SidebarGroup,
        ]
      : []),
    ...(dashboardMenuVisible
      ? [
          {
            items: [{ label: '대시보드', to: '/dashboards', icon: LayoutDashboard }],
          } satisfies SidebarGroup,
        ]
      : []),
    {
      heading: '프로젝트',
      headingAction: projectHeaderAction,
      items: [],
      renderContent: (
        <ChatProjectsSection
          sessions={inProjects}
          activePath={location.pathname}
          renamingId={renamingId}
          onStartRename={(id) => setRenamingId(id)}
          onDoneRename={() => setRenamingId(null)}
          onEditProject={(p) => setProjectDialog({ open: true, project: p })}
        />
      ),
    },
    {
      heading: '최근 채팅',
      items: [],
      renderContent: <ul className="space-y-0.5">{unfiled.map(renderChatRow)}</ul>,
      scrollable: true,
    },
  ];

  const sidebarOpen = !collapsed;
  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="h-[100dvh] flex bg-background text-foreground overflow-hidden">
        {/* 데스크탑/태블릿: 사이드바가 채팅 영역을 push. 모바일: 채팅 영역 위에 overlay. */}
        {sidebarOpen && !isMobile ? (
          <AppSidebar
            brand={branding.serviceName}
            logoSrc={branding.logoUrl}
            groups={groups}
            activePath={location.pathname}
            width={256}
            footer={<UserChip allowThemeToggle />}
            onClose={toggle}
          />
        ) : null}
        <main className={cn('flex-1 flex flex-col min-w-0 relative')}>
          {/* opener 는 페이지 자체 헤더 (Session.tsx) / hero (Index.tsx) 안에 SidebarOpenerButton 으로 인라인 통합. */}
          <Outlet />
        </main>
        {/* 모바일 overlay 모드 — backdrop + fixed 사이드바 */}
        {sidebarOpen && isMobile ? (
          <>
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: backdrop 은 별도 close 버튼 (사이드바 헤더 X) 으로도 닫을 수 있음 */}
            <div
              role="button"
              tabIndex={-1}
              aria-label="사이드바 닫기"
              onClick={toggle}
              className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[1px]"
            />
            <div className="fixed inset-y-0 left-0 z-50 flex shadow-2xl">
              <AppSidebar
                brand={branding.serviceName}
                logoSrc={branding.logoUrl}
                groups={groups}
                activePath={location.pathname}
                width={256}
                footer={<UserChip allowThemeToggle />}
                onClose={toggle}
              />
            </div>
          </>
        ) : null}
        <ChatSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
        <ChatProjectFormDialog
          open={projectDialog.open}
          onOpenChange={(o) => setProjectDialog(o ? projectDialog : { open: false })}
          project={projectDialog.open ? projectDialog.project : undefined}
        />
      </div>
    </DndContext>
  );
}
